<template>
  <div class="saved-reports">
    <div class="d-flex flex-wrap align-items-center" style="gap: 8px;">
      <select class="form-control form-control-sm" style="max-width: 260px;" v-model="selectedId" @change="applySelected">
        <option value="">{{ reports.length ? "Load a saved filter…" : "No saved filters yet" }}</option>
        <option v-for="r in reports" :key="r.id" :value="r.id">{{ r.name }}{{ r.schedule !== "none" ? ` (${r.schedule})` : "" }}</option>
      </select>
      <button class="btn btn-sm btn-outline-primary" @click="openForm(null)"><i class="fe fe-save mr-1"></i> Save current filters</button>
      <button v-if="reports.length" class="btn btn-sm btn-outline-secondary" @click="manage = !manage">
        <i class="fe fe-settings mr-1"></i> {{ manage ? "Close" : "Manage" }}
      </button>
    </div>

    <div v-if="editing" class="saved-form mt-3">
      <div class="form-row">
        <div class="form-group col-md-4">
          <label class="small font-weight-bold text-secondary">Name</label>
          <input class="form-control form-control-sm" v-model="draft.name" placeholder="e.g. BHCPF enrollees by facility" />
        </div>
        <div class="form-group col-md-4">
          <label class="small font-weight-bold text-secondary">Enrolment dates when it runs</label>
          <select class="form-control form-control-sm" v-model="draft.date_mode">
            <option value="saved">As saved</option>
            <option value="last_7_days">Last 7 days</option>
            <option value="last_30_days">Last 30 days</option>
            <option value="last_month">Last calendar month</option>
            <option value="all">All dates</option>
          </select>
        </div>
        <div class="form-group col-md-4">
          <label class="small font-weight-bold text-secondary">Email the Excel</label>
          <select class="form-control form-control-sm" v-model="draft.schedule">
            <option value="none">Don't schedule</option>
            <option value="daily">Every day (7:00)</option>
            <option value="weekly">Every week (7:00)</option>
            <option value="monthly">On the 1st of each month (7:00)</option>
          </select>
        </div>
        <div class="form-group col-md-4" v-if="draft.schedule === 'weekly'">
          <label class="small font-weight-bold text-secondary">Day</label>
          <select class="form-control form-control-sm" v-model.number="draft.weekday">
            <option v-for="(d, i) in days" :key="d" :value="i + 1">{{ d }}</option>
          </select>
        </div>
        <div class="form-group col-md-4" v-if="draft.schedule !== 'none'">
          <label class="small font-weight-bold text-secondary">Send to</label>
          <input type="email" class="form-control form-control-sm" v-model="draft.email" />
        </div>
      </div>
      <div class="d-flex" style="gap: 8px;">
        <button class="btn btn-sm btn-submit" @click="save" :disabled="saving || !draft.name">{{ saving ? "Saving…" : draft.id ? "Update" : "Save" }}</button>
        <button class="btn btn-sm btn-outline-secondary" @click="editing = false">Cancel</button>
        <small v-if="draft.id" class="text-muted align-self-center">The current filters on the page replace the saved ones.</small>
      </div>
    </div>

    <div v-if="manage && !editing" class="table-responsive mt-3">
      <table class="table table-sm mb-0">
        <thead class="table-head">
          <tr><th class="table-th">Name</th><th class="table-th">Dates</th><th class="table-th">Schedule</th><th class="table-th">Last sent</th><th class="table-th"></th></tr>
        </thead>
        <tbody>
          <tr class="table-row" v-for="r in reports" :key="r.id">
            <td>{{ r.name }}</td>
            <td>{{ dateModes[r.date_mode] }}</td>
            <td>{{ r.schedule === "none" ? "—" : r.schedule + (r.schedule === "weekly" ? " (" + days[(r.weekday || 1) - 1] + ")" : "") }}<div v-if="r.email" class="small text-muted">{{ r.email }}</div></td>
            <td>{{ r.last_run_at || "—" }}<div v-if="r.next_run_at" class="small text-muted">next {{ r.next_run_at }}</div></td>
            <td class="text-right text-nowrap">
              <button class="btn btn-sm btn-outline-success mr-1" @click="run(r)" title="Export now and email it"><i class="fe fe-send"></i></button>
              <button class="btn btn-sm btn-outline-primary mr-1" @click="openForm(r)" title="Edit"><i class="fe fe-edit-2"></i></button>
              <button class="btn btn-sm btn-outline-danger" @click="remove(r)" title="Delete"><i class="fe fe-trash-2"></i></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
/**
 * Saved filter presets with optional scheduled Excel exports (GET/POST/PUT/DELETE saved-reports, POST saved-reports/{id}/run).
 * getFilters() returns the page's current request filters (+ _form, the form snapshot to restore);
 * "apply" is emitted with the saved filters when one is loaded.
 */
export default {
  name: "SavedReports",
  props: {
    type: { type: String, default: "enrollees" },
    getFilters: { type: Function, required: true },
  },
  data() {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    return {
      user,
      reports: [],
      selectedId: "",
      manage: false,
      editing: false,
      saving: false,
      draft: {},
      days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      dateModes: { saved: "As saved", last_7_days: "Last 7 days", last_30_days: "Last 30 days", last_month: "Last month", all: "All dates" },
    };
  },
  mounted() { this.load(); },
  methods: {
    load() {
      this.axios.get("/api/v1/auth/saved-reports", { params: { type: this.type } }).then((res) => { this.reports = res.data.data; }).catch(() => {});
    },
    applySelected() {
      const r = this.reports.find((x) => x.id === this.selectedId);
      if (r) {
        this.$emit("apply", JSON.parse(JSON.stringify(r.filters || {})));
        this.toast(`Loaded "${r.name}". Click Filter to run it.`);
      }
    },
    openForm(r) {
      this.draft = r
        ? { id: r.id, name: r.name, date_mode: r.date_mode, schedule: r.schedule, weekday: r.weekday || 1, email: r.email || this.user.email || "" }
        : { id: null, name: "", date_mode: "saved", schedule: "none", weekday: 1, email: this.user.email || "" };
      this.editing = true;
    },
    save() {
      this.saving = true;
      const body = { ...this.draft, type: this.type, filters: this.getFilters(), email: this.draft.schedule === "none" ? (this.draft.email || null) : this.draft.email };
      const req = this.draft.id ? this.axios.put(`/api/v1/auth/saved-reports/${this.draft.id}`, body) : this.axios.post("/api/v1/auth/saved-reports", body);
      req
        .then(() => { this.editing = false; this.toast("Saved."); this.load(); })
        .catch(this.fail)
        .finally(() => { this.saving = false; });
    },
    run(r) {
      this.axios.post(`/api/v1/auth/saved-reports/${r.id}/run`).then((res) => this.toast(res.data.message)).catch(this.fail);
    },
    remove(r) {
      if (!confirm(`Delete the saved filter "${r.name}"?`)) return;
      this.axios.delete(`/api/v1/auth/saved-reports/${r.id}`).then(() => { this.toast("Deleted."); this.load(); }).catch(this.fail);
    },
    toast(msg) {
      this.$toasted.success(msg, { position: "top-center", duration: 3000 });
    },
    fail(e) {
      const d = e.response && e.response.data;
      const msg = (d && (d.message || (d.errors && Object.values(d.errors)[0][0]))) || "Something went wrong.";
      this.$toasted.error(msg, { position: "top-center", duration: 4000 });
    },
  },
};
</script>

<style scoped>
@import "../reports.css";
.saved-reports { border-top: 1px dashed #e2e8f0; padding-top: 12px; margin-top: 12px; }
.saved-form { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; }
</style>
